import { Link } from 'react-router-dom';
import type { ShipmentQueueItem } from '../../types';
import { formatCurrency } from '../../utils/currency';
import { buildPrintPath } from '../../utils/labels';

interface BulkQueueTableProps {
  items: ShipmentQueueItem[];
  showActions: boolean;
  onRemove: (index: number) => void;
}

function BulkQueueTable({ items, showActions, onRemove }: BulkQueueTableProps) {
  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <span className="card-title">Queue</span>
          <span className="count-pill">{items.length}</span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <p>No addresses in queue.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Result</th>
                {showActions && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.address.id}>
                  <td data-label="Name">
                    <strong>{item.address.name}</strong>
                    {item.address.company && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                        {item.address.company}
                      </div>
                    )}
                  </td>
                  <td data-label="Destination">
                    {item.address.city}, {item.address.state} {item.address.zip}
                    {item.address.country && item.address.country !== 'US' ? ` ${item.address.country}` : ''}
                  </td>
                  <td data-label="Status">
                    {item.status === 'pending' && (
                      <span className="status-badge purchased">
                        <span className="dot" />
                        Pending
                      </span>
                    )}
                    {item.status === 'processing' && (
                      <span className="status-badge info">
                        <span className="dot" />
                        Processing…
                      </span>
                    )}
                    {item.status === 'completed' && (
                      <span className="status-badge success">
                        <span className="dot" />
                        Completed
                      </span>
                    )}
                    {item.status === 'failed' && (
                      <span className="status-badge error">
                        <span className="dot" />
                        Failed
                      </span>
                    )}
                  </td>
                  <td data-label="Result">
                    {item.status === 'completed' && item.result && (
                      <div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>
                          {item.result.tracking_code}
                        </div>
                        {item.selectedRate && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                            {item.selectedRate.carrier} - {formatCurrency(item.selectedRate.rate, item.selectedRate.currency)}
                          </div>
                        )}
                        {item.result.label_url && (
                          <Link
                            to={buildPrintPath(item.result.label_url, item.result.label_file_type)}
                            style={{ fontSize: '0.74rem' }}
                          >
                            Print label
                          </Link>
                        )}
                      </div>
                    )}
                    {item.status === 'failed' && item.error && (
                      <span style={{ color: 'var(--error-text)', fontSize: '0.76rem' }}>{item.error}</span>
                    )}
                  </td>
                  {showActions && (
                    <td data-label="Actions" style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-ghost btn-small"
                        onClick={() => onRemove(index)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BulkQueueTable;
